import React, { useEffect, useMemo, useState } from "react";

const FilterSidebarDom = ({
  flights = [],
  filters = {},
  onFilterChange
}) => {

  // -------------------------------
  // SAFE FILTER VALUES
  // -------------------------------
  const safe = {
    departureTimes: filters?.departureTimes || [],
    stops: filters?.stops || [],
    airlines: filters?.airlines || [],
    fareType: filters?.fareType || [],
  };

  const [stopsList, setStopsList] = useState([]);
  const [airlinesList, setAirlinesList] = useState([]);
  const [fareTypeList, setFareTypeList] = useState([]);

  // -------------------------------
  // HANDLE FILTER CHANGE
  // -------------------------------
  const handleChange = (type, value) => {

    const prev = safe[type] || [];

    const updated = prev.includes(value)
      ? prev.filter((v) => v !== value)
      : [...prev, value];

    onFilterChange({
      ...filters,
      [type]: updated
    });
  };

  // -------------------------------
  // GENERATE FILTER DATA
  // -------------------------------
  useEffect(() => {

    if (!Array.isArray(flights) || flights.length === 0) {
      setStopsList([]);
      setAirlinesList([]);
      setFareTypeList([]);
      return;
    }

    // Stops
    const stops = [
      ...new Set(
        flights.map((f) =>
          String(
            f?.stops ??
            f?.totalStops ??
            f?.segments?.[0]?.stop ??
            0
          )
        )
      ),
    ].sort();

    setStopsList(stops);

    // Airlines
    const airlines = [
      ...new Set(
        flights
          .map((f) => {
            const seg = f?.segments?.[0];

            return (
              seg?.airLineName ||
              seg?.carrierName ||
              seg?.airlineName ||
              f?.airlineName ||
              null
            );
          })
          .filter(Boolean)
      ),
    ].sort();

    setAirlinesList(airlines);

    // Fare Type
    const fareTypes = [
      ...new Set(
        flights.map((f) =>
          f?.fares?.[0]?.isRefundable
            ? "Refundable"
            : "Non-Refundable"
        )
      ),
    ];

    setFareTypeList(fareTypes);

  }, [flights]);

  // -------------------------------
  // DEPARTURE TIME SLOTS
  // -------------------------------
  const departureSlots = useMemo(() => {

    if (!Array.isArray(flights)) return [];

    const hours = flights
      .map((f) => f?.segments?.[0]?.departDateTime)
      .filter(Boolean)
      .map((dt) => new Date(dt).getHours());

    const slots = {
      morning: [5, 11],
      afternoon: [12, 17],
      evening: [18, 20],
      night: [21, 23],
      lateNight: [0, 4],
    };

    return Object.keys(slots).filter((key) =>
      hours.some(
        (h) => h >= slots[key][0] && h <= slots[key][1]
      )
    );

  }, [flights]);

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <div className="box_shadow mt-3">

      <div className="accordion" id="filterAccordionReturn">

        {/* Departure Time */}
        <div className="accordion-item">

          <h2 className="accordion-header">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              data-bs-toggle="collapse"
              data-bs-target="#returnDepTime"
            >
              Return Departure Time
            </button>
          </h2>

          <div id="returnDepTime" className="accordion-collapse collapse">
            <div className="accordion-body">

              {departureSlots.map((cat) => (

                <div className="form-check" key={cat}>

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`return-departure-${cat}`}
                    checked={safe.departureTimes.includes(cat)}
                    onChange={() =>
                      handleChange("departureTimes", cat)
                    }
                  />

                  <label
                    className="form-check-label text-capitalize"
                    htmlFor={`return-departure-${cat}`}
                  >
                    {cat === "lateNight" ? "Late Night" : cat}
                  </label>

                </div>

              ))}

            </div>
          </div>
        </div>

        {/* Stops */}
        <div className="accordion-item">

          <h2 className="accordion-header">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              data-bs-toggle="collapse"
              data-bs-target="#returnStopFilter"
            >
              Return Flight Stops
            </button>
          </h2>

          <div id="returnStopFilter" className="accordion-collapse collapse">

            <div className="accordion-body">

              {stopsList.map((stop) => (

                <div className="form-check" key={stop}>

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`return-stop-${stop}`}
                    checked={safe.stops.includes(String(stop))}
                    onChange={() =>
                      handleChange("stops", String(stop))
                    }
                  />

                  <label
                    className="form-check-label"
                    htmlFor={`return-stop-${stop}`}
                  >
                    {stop === "0" ? "Non Stop" : `${stop} Stop`}
                  </label>

                </div>

              ))}

            </div>
          </div>
        </div>

        {/* Airlines */}
        <div className="accordion-item">

          <h2 className="accordion-header">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              data-bs-toggle="collapse"
              data-bs-target="#returnAirlineFilter"
            >
              Return Airlines
            </button>
          </h2>

          <div id="returnAirlineFilter" className="accordion-collapse collapse">

            <div className="accordion-body">

              {airlinesList.map((airline) => (

                <div className="form-check" key={airline}>

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`return-airline-${airline}`}
                    checked={safe.airlines.includes(airline)}
                    onChange={() =>
                      handleChange("airlines", airline)
                    }
                  />

                  <label
                    className="form-check-label"
                    htmlFor={`return-airline-${airline}`}
                  >
                    {airline}
                  </label>

                </div>

              ))}

            </div>
          </div>
        </div>

        {/* Fare Type */}
        <div className="accordion-item">

          <h2 className="accordion-header">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              data-bs-toggle="collapse"
              data-bs-target="#returnFareFilter"
            >
              Return Fare Type
            </button>
          </h2>

          <div id="returnFareFilter" className="accordion-collapse collapse">

            <div className="accordion-body">

              {fareTypeList.map((fare) => (

                <div className="form-check" key={fare}>

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`return-fare-${fare}`}
                    checked={safe.fareType.includes(fare)}
                    onChange={() =>
                      handleChange("fareType", fare)
                    }
                  />

                  <label
                    className="form-check-label"
                    htmlFor={`return-fare-${fare}`}
                  >
                    {fare}
                  </label>

                </div>

              ))}

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FilterSidebarDom;